/* eslint-disable */
import React from "react";
import { CalendarMonth } from '../../../controls/CalendarMonth';
import { CalendarHierarchy, InputCalendarHierarchyItem } from '../../../controls/CalendarHierarchy';
import { ICalendarItem, useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal, uniq } from "../../../../../shared";

export const CalendarHierarchyControl: React.FC = () => {
    const [controlItems, setControlItems] = React.useState<InputCalendarHierarchyItem[] | undefined>();
    const [dictItems] = useDictionary(dictionaryService, "calendar-origin");
    const [controlValue, setControlValue] = React.useState<number[] | undefined>();
    const [calendarValue, setCalendarValue] = useParameter<number[]>("@calendar");
    const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

    React.useEffect(function initControlValue() {
        if (calendarValue?.length && controlValue === undefined && dictItems?.length) {
            const value = calendarValue.reduce((result, calendarId) => {
                const calendarFrequency = Number(String(calendarId)[0]);
                const [isYear, isWeek] = [yearFrequency, weekFrequency].map(frequency => frequency === calendarFrequency);
                if (isYear && !result.includes(calendarId)) {
                    result.push(calendarId);
                }
                if (isWeek) {
                    const weekItem = dictItems.find(item => +item.id === calendarId);
                    const monthId = weekItem?.getValue<number>('parent');
                    if (monthId && !result.includes(monthId)) {
                        result.push(monthId);
                    }
                }
                return result;
            }, [] as number[]);
            setControlValue(value);
        }
    }, [calendarValue, controlValue, dictItems]);

    React.useEffect(function updateControlsItemsByDictionaryItems() {
        const candiatesControlItems = dictItems?.map<InputCalendarHierarchyItem & { frequency: number }>(item => ({
            id: Number(item.id),
            parent: item.getValue<number>('parent'),
            name: item.getValue<string>('name') || '',
            frequency: item.getValue<number>('frequencyId')!,
        }));

        const [minYear, maxYear] = [120000101, Number(`1${new Date().getFullYear()}0101`)];
        const nextControlItems = candiatesControlItems?.reduce((result, item) => {
            if (item.frequency !== weekFrequency) {
                const itemId = item.id as number;
                if (item.frequency === yearFrequency && itemId >= minYear && itemId <= maxYear) {
                    result.push(item);
                } else if (item.frequency === monthFrequency) {
                    const paretQuartal = dictItems?.find(dictItem => equal(+dictItem.id, item.parent));
                    const parentYearId = paretQuartal?.getValue<number>('parent')!;
                    if (parentYearId >= minYear && parentYearId <= maxYear) {
                        item.parent = parentYearId;
                        result.push(item);
                    }
                }
            }
            return result;
        }, [] as typeof candiatesControlItems);

        setControlItems(nextControlItems);
    }, [JSON.stringify(dictItems)]);

    const handlerChange = React.useCallback((value: unknown) => {
        const monthesOrYearsIds = value as number[];
        setControlValue(monthesOrYearsIds);

        const weeksOrYearsIds = uniq(monthesOrYearsIds.flatMap(monthOrYearId => {
            const firstCharacter = monthOrYearId.toString().slice(0, 1);
            const frequency = Number(firstCharacter);
            const isMonth = frequency === monthFrequency;
            if (isMonth) {
                const yearId = +`1${String(monthOrYearId).slice(1, 5)}0101`;
                const monthWeeks = dictItems?.filter(candidateWeekItem => {
                    const isWeek = weekFrequency === candidateWeekItem.getValue<number>("frequencyId");
                    if (isWeek) {
                        const weekParentId = candidateWeekItem.getValue<number>("parent");
                        return weekParentId === monthOrYearId;
                    }
                    return false;
                });
                return [ yearId, monthOrYearId, ...monthWeeks?.map(week => Number(week.id)) || []];
            }
            const yearWeeks = dictItems?.filter(candidateItem => {
                const [isWeek, isMonth] = [weekFrequency, monthFrequency].map(frequency => 
                    frequency === candidateItem.getValue<number>("frequencyId")
                );
                if (isWeek || isMonth) {
                    const yearKey = String(monthOrYearId).slice(1, 5);
                    const candidateKey = candidateItem.id.slice(1, 5);
                    return yearKey === candidateKey;
                }
            }).map(item => Number(item.id)) || [];
            return [monthOrYearId, ...yearWeeks];
        }));

        if (weeksOrYearsIds) {
            setCalendarValue(weeksOrYearsIds);
        }
    }, [dictItems]);

    return <CalendarHierarchy selectableParent label="Calendar" multiple value={controlValue} onChange={handlerChange} items={controlItems} />;
};
